import { convertNumericColorToHex, numericColorBrightness } from 'app/infrastructure/universal/color';
import { FavouriteRowValue } from '../common/favourite-interfaces';
import { FavouriteDataAdapter } from '../common/favourite-data-adapter';
import {
  ChartSeriesDataOptionsBuilder
} from './chart-series-data-options-builder';
import { SeriesTypeColumnMappings, SeriesTypeBaseIndex } from './types';
import { MIN_NO_OF_CRITERIAS } from 'app/area/reports/containers/chart/components/attributes/constants';
import { chartType } from 'app/components/favourite-layout/constants';

interface SynergiHighchartsDataPoint extends Highcharts.PointOptionsObject {
  [key: string]: any;
  synDataRowIndex?: number;
  seriesType: string;
}

export class ChartPointDataOptionsBuilder {
  private dataAdapter: FavouriteDataAdapter;
  private seriesDataOptionsBuilder: ChartSeriesDataOptionsBuilder;

  constructor(
    dataAdapter: FavouriteDataAdapter,
    seriesDataOptionsBuilder: ChartSeriesDataOptionsBuilder
  ) {
    this.dataAdapter = dataAdapter;
    this.seriesDataOptionsBuilder = seriesDataOptionsBuilder;
  }

  public createDataPoints(
    options: Highcharts.Options,
    seriesCol: number,
    seriesValues: FavouriteRowValue[],
    seriesTypeBaseIndex: SeriesTypeBaseIndex,
    columnMap: SeriesTypeColumnMappings,
    xIndex: number
  ) {
    const ignoreNullValues = this.ignoreNullValuesForDataPoints(xIndex);
    const dataAdapter = this.dataAdapter;
    const { dataModel } = dataAdapter;

    if (options.chart.type === chartType.PIE && dataAdapter.selectionCount >= MIN_NO_OF_CRITERIAS) {
      this.createSeriesForMultidimensional(options);
      return;
    }

    dataModel.rows.forEach((row, index) => {
      if ((xIndex >= 0) && (row[xIndex].value == null) && ignoreNullValues) {
        return;
      }

      this.createDataPoint(options, index, seriesCol, seriesValues, row, seriesTypeBaseIndex, columnMap, xIndex);
      if (dataAdapter.hasRollingAverage() && columnMap.rollingAverageSeries) {
        this.createDataPoint(options, index, seriesCol, seriesValues, row, seriesTypeBaseIndex, columnMap, xIndex, 'rollingAverageSeries');
      }
      if (dataAdapter.hasTarget() && columnMap.targetSeries) {
        this.createDataPoint(options, index, seriesCol, seriesValues, row, seriesTypeBaseIndex, columnMap, xIndex, 'targetSeries');
      }
    });
  }

  private createDataPoint(
    options: Highcharts.Options,
    rowIndex: number,
    seriesCol: number,
    seriesValues: FavouriteRowValue[],
    row: FavouriteRowValue[],
    seriesTypeBaseIndex: SeriesTypeBaseIndex,
    columnMap: SeriesTypeColumnMappings,
    xIndex: number,
    seriesType: string = 'mainSeries'
  ) {
    let seriesIndex = seriesCol >= 0 ? seriesValues.findIndex(series => { return series.value === row[seriesCol].value }) : 0;
    seriesIndex += seriesTypeBaseIndex[seriesType];
    const dataArray = (options.series[seriesIndex] as any).data;
    const dataPoint: SynergiHighchartsDataPoint = { seriesType: seriesType };
    const dataAdapter = this.dataAdapter;
    for (let c = 0; c < columnMap[seriesType].length; c++) {
      const colType = columnMap[seriesType][c];
      if (colType === 'splitting' || colType === 'series' || !colType) {
        continue;
      }
      const rowVal = row[c];
      let val: any = rowVal.value;
      if ((colType === 'x' || colType === 'y') && dataAdapter.hasCategories(c)) {
        val = dataAdapter.columnCategories[c].findIndex(cat => { return val === cat.value });
      }
      if (this.seriesDataOptionsBuilder.shouldAddColorToDataPoint(seriesCol) && rowVal.bgColour) {
        dataPoint.color = convertNumericColorToHex(rowVal.bgColour);
        if (this.seriesDataOptionsBuilder.shouldAdjustDataPointLabelColor() && numericColorBrightness(rowVal.bgColour) < 128) {
          dataPoint.dataLabels = {
            ...(dataPoint.dataLabels || {}),
            color: '#ffffff'
          };
        }
      }
      dataPoint[columnMap[seriesType][c]] = val;
    }

    this.addStaticDataPointProperties(dataPoint);

    if (xIndex >= 0) {
      dataPoint.name = dataAdapter.format(xIndex, row[xIndex].value);
    }

    // We set our own row index property to easily identify which row in the data a point belongs to (used for drilldown)
    dataPoint.synDataRowIndex = rowIndex;

    (dataArray as Highcharts.PointOptionsObject[]).push(dataPoint);
  }

  public ignoreNullValuesForDataPoints(axisColIndex: number): boolean {
    return !this.dataAdapter.hasCategories(axisColIndex);
  }

  public addStaticDataPointProperties(dataPoint: Highcharts.PointOptionsObject) {
    return;
  }

  private createSeriesForMultidimensional(options: Highcharts.Options): void {
    const dataAdapter = this.dataAdapter;
    const { layoutAttributes } = dataAdapter.layoutConfig;
    const pieAttributes = layoutAttributes.Pie;
    const isInDashboardContext = layoutAttributes.Highcharts.plotOptions.pie?.showInLegend;
    const isHideDatalabelsEnabled = pieAttributes?.HideDatalabelsOnDashboards !== false ||
      isInDashboardContext && pieAttributes?.ShowValue === false && pieAttributes?.ShowPercent === false;
    const events = (options.series as Highcharts.PlotPieOptions[])?.[0]?.events;
    
    options.title.text = '';
    options.plotOptions.pie.center = ['50%', '50%'];
    if (!isInDashboardContext) {
       options.legend.margin = 50;
    }
    options.plotOptions.pie.point = {
      ...options.plotOptions.pie?.point,
      events: { 
        ...options.plotOptions.pie?.point?.events,
        legendItemClick: (e: any) => {
          e.preventDefault();
          return false;
        }
      }
    };
    options.series = [];

    for (let i = 0; i < dataAdapter.selectionCount; i++) {
      const aggregatedValues = dataAdapter.dataModel.rows?.reduce((acc, row) => {
        acc[row[i]?.value] = { 
          value: Number(acc[row[i]?.value]?.value || 0) + row[dataAdapter.selectionCount]?.value,
          bgColour: row[0]?.bgColour,
          name: row[i]?.value
        };
        return acc;
      }, {});

      if (i < dataAdapter.selectionCount - 1) {
        const aggregatedSeries: Highcharts.SeriesOptionsRegistry['SeriesPieOptions'] = {
          name: dataAdapter.dataModel.columns[i].shortText,
          data: Object.keys(aggregatedValues).map(row => ({
            name: dataAdapter.format(i, aggregatedValues[row]?.name),
            x: dataAdapter.format(i, aggregatedValues[row]?.name) as any,
            y: aggregatedValues[row]?.value,
            color: aggregatedValues[row].bgColour ? convertNumericColorToHex(aggregatedValues[row]?.bgColour) : undefined
          })),
          size: '60%',
          type: 'pie',
          dataLabels: {
            enabled: false
          },
          showInLegend: true,
          events: {
            click: (e: any) => {
              e.preventDefault();
            }
          }
        };
        options.series.push(aggregatedSeries);
      } else {
        const lastSelectionSeries: Highcharts.SeriesOptionsRegistry['SeriesPieOptions'] = {
          name: dataAdapter.dataModel.columns[i + 1].shortText,
          data: dataAdapter.dataModel.rows?.map(
            (row, rowIndex) => this.getDataPoint(
              row,
              dataAdapter.selectionCount,
              rowIndex
            )
          ),
          size: '80%',
          innerSize: '60%',
          type: 'pie',
          dataLabels: {
            ...options.plotOptions.pie.dataLabels,
            enabled: !isHideDatalabelsEnabled
          },
          events,
          showInLegend: false
        };
        options.series.push(lastSelectionSeries);
      }
    }
  }

  private getDataPoint(row: FavouriteRowValue[], selectionCount: number, rowIndex: number): SynergiHighchartsDataPoint {
    const rowVal = row[selectionCount];
    const rowSeries = row[selectionCount - 1];
    const dataPoint: SynergiHighchartsDataPoint = { seriesType: 'mainSeries' };
    dataPoint.y = rowVal?.value;
    dataPoint.x = row[0]?.value;
    dataPoint.name = this.dataAdapter.format(1, rowSeries?.value);
    dataPoint.synDataRowIndex = rowIndex;
    dataPoint.color = !!rowSeries?.bgColour ? convertNumericColorToHex(rowSeries?.bgColour) : undefined;
    dataPoint.colType = 'y';
    dataPoint.splitting = row[1]?.value;
   
    return dataPoint;
  }
}
